//
// ============================================================================
// IMPORTS
// ============================================================================
//

// Types
import type { PendingSample } from "../services/api/domains/pending/pending.types";

/**
 * Sort samples by priority (STAT first), then oldest first
 *
 * Sorting order:
 * 1. STAT samples appear first (high priority)
 * 2. Within each group, oldest samples first (by taken timestamp)
 * 3. Samples with same timestamp maintain their original order
 *
 * @param samples - Array of pending samples to sort
 * @param isStatChecker - Function to check if barcode is STAT
 * @returns New sorted array (original array unchanged)
 */
export function sortSamplesByPriority(
  samples: PendingSample[],
  isStatChecker: (barcode: string) => boolean,
): PendingSample[] {
  // Add original index to each sample for stable sorting
  const samplesWithIndex = samples.map((sample, index) => ({
    sample,
    originalIndex: index,
  }));

  return samplesWithIndex
    .sort((a, b) => {
      // Priority 1: STAT samples first
      const aIsStat = isStatChecker(a.sample.barcode);
      const bIsStat = isStatChecker(b.sample.barcode);
      if (aIsStat !== bIsStat) return aIsStat ? -1 : 1;

      // Priority 2: Oldest first (by taken timestamp)
      const aTime = a.sample.taken ? new Date(a.sample.taken).getTime() : 0;
      const bTime = b.sample.taken ? new Date(b.sample.taken).getTime() : 0;

      if (aTime !== bTime) {
        // Samples without timestamp (0) go to end
        if (aTime === 0) return 1;
        if (bTime === 0) return -1;
        return aTime - bTime;
      }

      // Priority 3: Maintain original order using index (stable sort)
      return a.originalIndex - b.originalIndex;
    })
    .map(({ sample }) => sample);
}
